import {Events, IAsyncUploader} from '@src/@interfaces/services/Http/IAsyncUploader';
import {randomString} from '@src/helpers/rand';
import {IListener} from '@src/@interfaces/services/Events';
import {IAsyncUploaderFileMeta} from '@src/@interfaces/services/Http/IAsyncUploaderFileMeta';
import {IAsyncUploaderStatusRequest} from '@src/@interfaces/services/Http/IAsyncUploaderStatusRequest';
import {IAsyncUploaderStatusResponse} from '@src/@interfaces/services/Http/IAsyncUploaderStatusResponse';
import {IAsyncUploaderQueuedFile} from '@src/@interfaces/services/Http/IAsyncUploaderQueuedFile';
import AsyncUploaderQueuedFile from './AsyncUploaderQueuedFile';
import {ISubscription} from '@src/@interfaces/services/Events/ISubscription';
import app from '@src/helpers/app';
import {HTTPMethods} from '@src/@interfaces/http/HTTPMethods';
import config from '@src/helpers/config';
import {parseJsonSilently} from '@src/helpers/json/parseJsonSilently';

// TODO: refactor workflow. There are mess with errors catching
export default class AsyncUploader implements IAsyncUploader {
    protected statusRouteName = 'uploader.status';
    protected writeRouteName = 'uploader.write';

    protected files: File[] = [];
    protected filesRequests: Record<string, XMLHttpRequest> = {};
    protected filesMeta: Record<string, IAsyncUploaderFileMeta> = {};
    protected filesItems: Record<string, IAsyncUploaderQueuedFile> = {};

    protected filesServerHashToUniqueIdMap: Record<string, string> = {};

    protected processorId: ReturnType<typeof setTimeout> | null = null;
    protected processorTickFrequency = 250;
    protected processorLastTickTime = 0;

    protected uploadsInProgress = 0;
    protected maxNumberOfParallelUploads: number | null = null;// will be gotten from config
    protected lastUploadsInProgressValidationTime = 0;
    protected uploadsInProgressValidationFrequency = 5000;

    protected isPaused = false;
    protected mode: 'stream' | 'partial' = 'partial';
    protected chunkSizeValue: number | null = null;// will be gotten from config

    protected progressShift = 5;// in percents
    protected currentProgress = 0;
    protected currentProgressLastCalTime = 0;
    protected currentProgressCalculationDelay = 1000 / 4;// 4 - updates per second

    protected fatalException: Error | null = null;

    getProgress(): number {
        if ((Date.now() - this.currentProgressLastCalTime) < this.currentProgressCalculationDelay) {
            return this.currentProgress;
        }

        this.currentProgressLastCalTime = Date.now();
        const [uploadedBytes, totalBytes] = Object.keys(this.filesMeta).reduce((res, hash) => {
            const meta = this.filesMeta[hash];
            if (!meta.isLoading || meta.isPaused || meta.error) {
                return res;
            }

            return [
                res[0] + meta.sentBytes,
                res[1] + meta.totalBytes,
            ];
        }, [0, 0]);

        return this.currentProgress = this.calculateProgress(uploadedBytes, totalBytes);
    }

    getProgressOfItem(uniqueId: string): number {
        const meta = this.getMeta(uniqueId);

        return meta ? this.calculateProgress(meta.sentBytes, meta.totalBytes) : 0;
    }

    getMaxNumberOfParallelUploads(): number {
        return this.maxNumberOfParallelUploads ??= config('client.async-uploader.max-parallel-uploads', 1);
    }

    setMaxNumberOfParallelUploads(number: number): void {
        this.maxNumberOfParallelUploads = number;
    }

    getItems(): IAsyncUploaderQueuedFile[] {
        return Object.keys(this.filesMeta).map(
            key => this.getItem(key) as IAsyncUploaderQueuedFile
        );
    }

    getItemsToLoad(): IAsyncUploaderQueuedFile[] {
        return this.getItems().filter(item => {
            const meta = item.getMeta();

            return !meta.isPaused && !meta.error;
        });
    }

    getLoadingNowItems(): IAsyncUploaderQueuedFile[] {
        return this.getItems().filter(item => {
            const meta = item.getMeta();

            return meta.isLoading && !meta.isPaused && !meta.error;
        });
    }

    getItem(uniqueId: string): IAsyncUploaderQueuedFile | null {
        if (!this.has(uniqueId)) {
            return null;
        }

        return this.filesItems[uniqueId] ??= new AsyncUploaderQueuedFile(this, this.filesMeta[uniqueId]);
    }

    getItemByServerHash(serverHash: string): IAsyncUploaderQueuedFile | null {
        if (serverHash in this.filesServerHashToUniqueIdMap) {
            return this.getItem(this.filesServerHashToUniqueIdMap[serverHash]);
        }

        return null;
    }

    getFile(uniqueId: string): File | null {
        const index = this.filesMeta[uniqueId]?.index;

        return index == null ? null : (this.files[index] ?? null);
    }

    getMeta(uniqueId: string): IAsyncUploaderFileMeta | null {
        return this.filesMeta[uniqueId] ?? null;
    }

    push(file: File, context: IAsyncUploaderFileMeta['context'] = null): IAsyncUploaderQueuedFile {
        const meta: IAsyncUploaderFileMeta = {
            error: null,
            serverHash: undefined,
            uniqueId: this.getFileUniqueId(file),
            totalBytes: file.size,
            sentBytes: 0,
            writtenBytes: 0,
            index: this.files.push(file) - 1,
            isLoading: false,
            loading_from: null,
            isPaused: false,
            paused_from: null,
            updated_at: 0,
            context: context,
        };

        this.filesMeta[meta.uniqueId] = meta;

        this.events.dispatch(Events.PUSH, meta);
        this.events.dispatch(Events.FILE_ADDED_TO_LIST, {uniqueId: meta.uniqueId});

        setTimeout(() => void this.processQueue(), 10);

        return this.getItem(meta.uniqueId) as IAsyncUploaderQueuedFile;
    }

    has(uniqueId: string): boolean {
        return (uniqueId in this.filesMeta) && (this.filesMeta[uniqueId].index in this.files);
    }

    remove(uniqueId: string): boolean {
        const index = this.filesMeta[uniqueId]?.index;
        if (!index) {
            return true;
        }

        this.abort(uniqueId);
        this.files.splice(index, 1);
        delete this.filesMeta[uniqueId];
        delete this.filesServerHashToUniqueIdMap[uniqueId];

        this.events.dispatch(Events.REMOVE, {uniqueId});
        this.events.dispatch(Events.FILE_REMOVED_FROM_LIST, {uniqueId});

        return true;
    }

    pause(uniqueId: string): boolean {
        if (!this.has(uniqueId)) {
            return false;
        }

        this.abort(uniqueId);
        this.filesMeta[uniqueId].isPaused = true;
        this.filesMeta[uniqueId].paused_from = Date.now();
        this.filesMeta[uniqueId].updated_at = Date.now();

        this.events.dispatch(Events.PAUSE, {uniqueId});

        return true;
    }

    resume(uniqueId: string): boolean {
        if (!this.has(uniqueId)) {
            return false;
        }

        this.filesMeta[uniqueId].isPaused = false;
        this.filesMeta[uniqueId].paused_from = null;
        this.filesMeta[uniqueId].updated_at = Date.now();

        this.events.dispatch(Events.RESUME, {uniqueId});
        this.processQueue();

        return true;
    }

    abort(uniqueId: string): boolean {
        if (
            !this.has(uniqueId) ||
            !this.filesRequests[uniqueId] ||
            !this.filesMeta[uniqueId].isLoading
        ) {
            return false;
        }

        this.filesRequests[uniqueId].abort();
        this.filesMeta[uniqueId].isLoading = false;
        this.filesMeta[uniqueId].loading_from = null;
        this.filesMeta[uniqueId].updated_at = Date.now();
        delete this.filesRequests[uniqueId];

        this.events.dispatch(Events.ABORT, {uniqueId});

        return true;
    }

    listen<TPayload>(event: Events | Events[], listener: IListener<TPayload>): ISubscription {
        return this.events.listen(event, listener);
    }

    protected get chunkSize() {
        if (this.chunkSizeValue !== null) {
            return this.chunkSizeValue;
        }

        const mb = config('client.async-uploader.max-chunk-size-in-mb', 50);

        return this.chunkSizeValue = mb * 1024 * 1024;
    }

    protected processQueue(): void {
        if (this.fatalException) {
            this.events.dispatch(Events.ERROR_OCCURRED, {uniqueId: null, error: this.fatalException});

            throw this.fatalException;
        }

        if (!this.isProcessingNow()) {
            this.processorTick();
        }
    }

    protected terminateProcessing(): void {
        if (this.processorId) {
            clearTimeout(this.processorId);
        }

        this.processorId = null;
    }

    protected restartQueueProcessing(): void {
        this.terminateProcessing();
        this.processQueue();
    }

    protected processorTick(): void {
        this.validateUploadsInProgress();

        this.processorLastTickTime = Date.now();

        if (!this.isPaused && this.uploadsInProgress < this.getMaxNumberOfParallelUploads()) {
            const fileToUploadMeta = Object.values(this.filesMeta).filter(
                item => !item.isLoading && !item.isPaused && !item.error
            ).sort((a, b) => {
                return a.totalBytes - b.totalBytes;
            }).at(0);
            if (!fileToUploadMeta) {
                // queue is empty
                this.terminateProcessing();

                return;
            }

            this.processSending(fileToUploadMeta.uniqueId).then();
        }

        if (this.fatalException) {
            return;
        }

        this.terminateProcessing();

        this.processorId = setTimeout(
            this.processorTick.bind(this),
            this.processorTickFrequency
        );
    }

    protected forgetUploadedFile(uniqueId: string): void {
        const index = this.filesMeta[uniqueId]?.index;

        delete this.filesMeta[uniqueId];
        delete this.filesRequests[uniqueId];
        delete this.filesItems[uniqueId];
        delete this.filesServerHashToUniqueIdMap[uniqueId];
        if (index != null) {
            delete this.files[index];
        }

        this.events.dispatch(Events.FILE_REMOVED_FROM_LIST, {uniqueId});
    }

    protected async processSending(uniqueId: string): Promise<boolean> {
        this.uploadsInProgress++;
        this.events.dispatch(Events.SEND, {uniqueId});

        try {
            const meta = await this.syncFileMetadataWithServer(uniqueId);
            const url = this.getUrlToSendFile();

            if (uniqueId in this.filesRequests) {
                this.filesRequests[uniqueId].abort();
            }

            let promise;
            if (this.mode === 'stream') {
                promise = this.sendFileAsStream(meta, url);
            } else if (this.mode === 'partial') {
                promise = this.sendFilePartially(meta, url, Math.floor(this.chunkSize * 0.97));// for FormData bites
            } else {
                throw new Error('Unknown type of transportation');
            }

            return promise.then(result => {
                this.uploadsInProgress--;

                return result;
            }).catch(e => {
                if (e instanceof Error && (uniqueId in this.filesMeta)) {
                    this.filesMeta[uniqueId].error = e;
                    this.events.dispatch(Events.ERROR_OCCURRED, {uniqueId: uniqueId, error: e});
                }

                throw e;
            });
        } catch (e) {
            this.uploadsInProgress--;
            this.filesMeta[uniqueId].isLoading = false;
            this.filesMeta[uniqueId].loading_from = null;

            if (e instanceof Error) {
                this.filesMeta[uniqueId].error = e;
            }

            this.events.dispatch(Events.ERROR_OCCURRED, {uniqueId: uniqueId, error: e});

            throw e;
        }
    }

    protected async fetchFileStatus(uniqueId: string): Promise<IAsyncUploaderStatusResponse> {
        const file = this.getFile(uniqueId);
        if (!file) {
            throw new Error('Not found');
        }

        return this.app.make('http').route(this.statusRouteName, undefined, {
            headers: {'content-type': 'application/json'},
            body: {
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified,
                context: this.getMeta(uniqueId)?.context,
            } as IAsyncUploaderStatusRequest
        }).then(res => {
            this.events.dispatch(Events.STATUS_RECEIVED, {uniqueId, status: res});

            return res;
        });
    }

    protected async sendFileAsStream(meta: Required<IAsyncUploaderFileMeta>, url: string) {
        const uniqueId = meta.uniqueId;

        const xhr = this.filesRequests[uniqueId] = this.makeOpenedRequest(HTTPMethods.POST, url);
        xhr.setRequestHeader('X-File-Hash', meta.serverHash);
        xhr.setRequestHeader('X-File-Start-Byte', String(meta.writtenBytes));

        xhr.upload.addEventListener('progress', e => {
            this.filesMeta[uniqueId].sentBytes = e.loaded;
            this.filesMeta[uniqueId].updated_at = Date.now();

            this.events.dispatch(Events.PROGRESS, {uniqueId, event: e});
        });

        return new Promise<boolean>((resolve, reject) => {
            const listener = () => {
                this.filesMeta[uniqueId].isLoading = false;
                this.filesMeta[uniqueId].loading_from = null;
                this.filesMeta[uniqueId].updated_at = Date.now();

                if (xhr.status === 200) {
                    this.events.dispatch(Events.SUCCESS, {uniqueId});
                    this.forgetUploadedFile(uniqueId);

                    resolve(true);

                    return;
                }

                this.events.dispatch(Events.FAIL, {uniqueId});
                reject(new Error('Upload filed'));
            };

            xhr.addEventListener('load', listener);
            xhr.addEventListener('error', listener);
            xhr.addEventListener('abort', () => resolve(false));

            const file = this.files[meta.index];
            const formData = new FormData();
            formData.set('file', file.slice(meta.writtenBytes, file.size, file.type), file.name);

            xhr.send(formData);
        }).finally(() => {
            this.events.dispatch(Events.COMPLETE, {uniqueId, meta});
        });
    }

    protected async sendFilePartially(meta: Required<IAsyncUploaderFileMeta>, url: string, chunkSize: number): Promise<boolean> {
        const uniqueId = meta.uniqueId;

        const makeOpenedRequest = () => this.filesRequests[uniqueId] = this.makeOpenedRequest(HTTPMethods.POST, url);

        const dispatcher = this.events;
        const file = this.files[meta.index];

        const onComplete = (error: Error | null = null) => {
            meta.isLoading = false;
            meta.loading_from = null;
            meta.updated_at = Date.now();
            meta.error = error;
        };

        return await (async function sendNextPart(sentBytes: number): Promise<boolean> {
            const bytesToSend = file.size - sentBytes;
            if (bytesToSend <= 0) {
                meta.isLoading = false;
                meta.loading_from = null;
                meta.updated_at = Date.now();

                return true;
            }

            const chunkStart = sentBytes;
            // last byte of chunkEnd won't be included in new blob, so + 1
            // for details see https://developer.mozilla.org/ru/docs/Web/API/Blob/slice
            const chunkEnd = Math.min(file.size, chunkStart + chunkSize) + 1;

            const xhr = makeOpenedRequest();
            xhr.setRequestHeader('X-File-Hash', meta.serverHash);
            xhr.setRequestHeader('X-File-Start-Byte', String(sentBytes));
            xhr.setRequestHeader('Content-Range', `bytes ${chunkStart}-${chunkEnd - 1}/${file.size}`);

            const formData = new FormData();
            formData.set('file', file.slice(chunkStart, chunkEnd, file.type), file.name);

            xhr.upload.addEventListener('progress', e => {
                meta.sentBytes = sentBytes + e.loaded;
                meta.updated_at = Date.now();

                dispatcher.dispatch(Events.PROGRESS, {uniqueId, event: e});
            });

            return await new Promise<boolean>((resolve, reject) => {
                const listener = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve(true);
                    } else {
                        reject(new Error(parseJsonSilently(xhr.responseText)?.message ?? 'Upload failed'));
                    }
                };

                xhr.addEventListener('load', listener);
                xhr.addEventListener('error', listener);
                xhr.addEventListener('abort', () => resolve(false));

                xhr.send(formData);
            }).then(value => {
                if (value) {
                    return sendNextPart(chunkEnd);
                }

                return value;
            });
        })(meta.writtenBytes)
            .then(result => {
                onComplete();
                dispatcher.dispatch(Events.SUCCESS, {uniqueId});
                this.forgetUploadedFile(uniqueId);

                return result;
            }).catch(e => {
                onComplete(e);
                this.events.dispatch(Events.FAIL, {uniqueId});

                return Promise.reject(e);
            }).finally(() => {
                this.events.dispatch(Events.COMPLETE, {uniqueId, meta});
            });
    }

    protected async syncFileMetadataWithServer(uniqueId: string): Promise<Required<IAsyncUploaderFileMeta>> {
        const meta = this.filesMeta[uniqueId];

        meta.isLoading = true;
        meta.loading_from = Date.now();

        try {
            const status = await this.fetchFileStatus(uniqueId);
            meta.serverHash = status.hash;
            meta.writtenBytes = status.size;
            meta.updated_at = Date.now();

            this.filesServerHashToUniqueIdMap[meta.serverHash] = uniqueId;

            this.events.dispatch(Events.STATUS_UPDATED, {uniqueId, meta});

            return meta as Required<IAsyncUploaderFileMeta>;
        } catch (e) {
            if (e instanceof Error) {
                // TODO: handle network cases
                meta.error = e;
                this.events.dispatch(Events.ERROR_OCCURRED, {uniqueId, error: e});
            }

            this.terminateProcessing();

            throw e;
        }
    }

    protected getUrlToSendFile(): string {
        const url = this.app.make('url').route(this.writeRouteName);
        if (!url) {
            throw new Error('Can\'t resolve URL to write file');
        }

        return url;
    }

    protected validateUploadsInProgress(): void {
        if (this.lastUploadsInProgressValidationTime > Date.now() - this.uploadsInProgressValidationFrequency) {
            return;
        }

        this.uploadsInProgress = Object.values(this.filesMeta).filter(item => item.isLoading).length;
        this.lastUploadsInProgressValidationTime = Date.now();
    }

    protected isProcessingNow(): boolean {
        return this.processorId != null && this.processorLastTickTime > (Date.now() - (this.processorTickFrequency + 100));
    }

    protected getFileUniqueId(file: File): string {
        return `${file.name}-${file.size}-${file.lastModified}-${file.type}-${randomString(12)}`;
    }

    protected makeOpenedRequest(method: HTTPMethods, url: string) {
        const xhr = new XMLHttpRequest();

        xhr.open(method, url, true);
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.setRequestHeader('credentials', 'include');
        xhr.setRequestHeader('Authorization', `Bearer ${app('auth').getAccessToken()}`);
        xhr.setRequestHeader('X-CSRF-TOKEN', app('CSRFManager').CSRFToken);
        xhr.setRequestHeader('X-XSRF-TOKEN', app('CSRFManager').XSRFToken);

        return xhr;
    }

    protected calculateProgress(uploadedBytes: number, totalBytes: number): number {
        const percent = (uploadedBytes * (1 + this.progressShift / 100)) / (totalBytes / 100);

        return Math.max(0, Math.min(100, percent));
    }

    protected get app() {
        return app();
    }

    protected get events() {
        return this.app.make('events');
    }
}
